<template>
  <div
    class="multiscenario-pump-chart"
    :class="{ 'is-static': isStatic, 'is-horizontal': isHorizontal }"
  >
    <DDV
      v-slot="{ margins, onUpdateNearestPoint, enableMouseTracking }"
      v-bind="$attrs"
      class="multiscenario-pump"
      @update:dom-state="domState = $event"
    >
      <template
        v-if="domState && domState.plotWidth > 0 && domState.plotHeight > 0"
      >
        <rect
          class="plotBg"
          :x="margins.left"
          :y="margins.top"
          :width="domState?.plotWidth"
          :height="domState?.plotHeight"
        />

        <g v-if="readyToDraw && activeDataSets.length > 0">
          <Axis
            :label="`In Situ Flow Rate (${xUnitLabel})`"
            :scaler="xScaler"
            :domain="aggregateX"
            :x="margins.left"
            :y="margins.top"
            :tick-frequency="ddvTickFrequencies?.x"
            draw-grid-lines
          />
          <Axis
            :label="yUnitLabel"
            :scaler="yScaler"
            :domain="paddedDomainY"
            :x="margins.left"
            :y="margins.top"
            :tick-frequency="ddvTickFrequencies?.y"
            is-vertical
            draw-grid-lines
          />
          <g
            v-if="(domState?.plotWidth || 0) > 0"
            :transform="`translate(${margins.left} ${margins.top})`"
          >
            <BSP
              v-for="(ds, i) in producedTdhDataSets"
              v-slot="{ screenPoints }"
              :key="`produced-tdh-plot-${i}`"
              :points="ds.points"
              :screen-points="
                ds.points.map(p => ({
                  x: xScaler.scale(p.x),
                  y: yScaler.scale(p.y)
                }))
              "
            >
              <g
                :class="`additional-plot-${i}`"
                :style="{ strokeDasharray: i * 2.5 + 5 }"
              >
                <LinePlot
                  class="produced-tdh-line"
                  :style="`--activePlotColor: ${ds.color}`"
                  :data-series-number="i + 1"
                  :x="margins.left"
                  :y="margins.top"
                  :screen-points="screenPoints"
                  curve-interpolator="catmull-rom"
                />
              </g>
            </BSP>

            <BSP
              v-for="(ds, i) in requiredTdhDataSets"
              v-slot="{ nearestPoint, screenPoints }"
              :key="`additional-plot-${i}`"
              :points="ds.points"
              :screen-points="
                ds.points.map(p => ({
                  x: xScaler.scale(p.x),
                  y: yScaler.scale(p.y)
                }))
              "
              @update:nearest-point="
                enableMouseTracking &&
                  onUpdateNearestPoint(
                    ds.title ?? '',
                    i,
                    true,
                    $event,
                    formatValue,
                    formatValue,
                    ds.xAxisLabel,
                    ds.yAxisLabel
                  )
              "
            >
              <g :class="`additional-plot-${i}`">
                <LinePlot
                  class="additional-plot-line"
                  :style="`--activePlotColor: ${ds.color}`"
                  :data-series-number="i"
                  :x="margins.left"
                  :y="margins.top"
                  :screen-points="screenPoints"
                  curve-interpolator="catmull-rom"
                />

                <NearestPoint
                  v-if="nearestPoint?.node && !isStatic"
                  class="additional-plot-point"
                  :data-series-number="i"
                  :nearest-point="nearestPoint"
                />
              </g>
            </BSP>

            <g
              v-for="p in operatingPoints"
              :key="p.label"
              :transform="`
              translate(
                ${xScaler.scale(p.point.x)},
                ${yScaler.scale(p.point.y)}
              )`"
            >
              <g>
                <circle
                  :cx="0"
                  :cy="0"
                  :r="5"
                  :stroke="p.color"
                  :stroke-width="2"
                  fill="transparent"
                />
                <circle
                  :cx="0"
                  :cy="0"
                  :r="5"
                  :stroke="p.color"
                  :stroke-opacity="0.3"
                  :stroke-width="8"
                  fill="transparent"
                />
                <circle :cx="0" :cy="0" :r="4" fill="var(--plotSurface)" />
              </g>
            </g>
          </g>

          <ChartTitle v-if="title" :title="title" />
        </g>
        <g v-else>
          <text
            fill="var(--axisLabelColor)"
            :x="(domState?.plotWidth || 0) / 2"
            :y="(domState?.plotHeight || 0) / 2"
            text-anchor="middle"
          >
            No Data
          </text>
        </g>
      </template>
    </DDV>
    <div v-for="(pt, row) in producedTdhDataSets" :key="pt.title">
      <CustomLegend
        v-if="drawLegend && pt.points.length > 0"
        v-model="activeDataSets"
        :static="isStatic"
        :horizontal="isHorizontal"
        :model="[pt]"
      />
      <div class="flex justify-content-around">
        <CustomLegend
          v-if="drawLegend && pt.points.length > 0"
          v-model="activeDataSets"
          :static="isStatic"
          :horizontal="isHorizontal"
          :model="operatingPoints.filter((_, idx) => idx === row * 3 + 0)"
        />
        <CustomLegend
          v-if="drawLegend && pt.points.length > 0"
          v-model="activeDataSets"
          :static="isStatic"
          :horizontal="isHorizontal"
          :model="operatingPoints.filter((_, idx) => idx === row * 3 + 1)"
        />
        <CustomLegend
          v-if="drawLegend && pt.points.length > 0"
          v-model="activeDataSets"
          :static="isStatic"
          :horizontal="isHorizontal"
          :model="operatingPoints.filter((_, idx) => idx === row * 3 + 2)"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { DataSet, DOMState } from "@incytestudios/ddv/types/common";
import {
  DDV,
  BSP,
  LinePlot,
  NearestPoint,
  ChartTitle,
  Axis,
  useAggregateDomains,
  useLinearScaler,
  getDefaultValueFormatter,
  isNumberDomain,
  fromRange,
  type NumericDomain
} from "@incytestudios/ddv";
import type { ScenarioMap } from "@/components/types";
import type { Sizing } from "@/composables/forms/formTypes";

import { computed, inject, ref, watch } from "vue";
import CustomLegend from "./CustomLegend.vue";
import { injectStrict } from "@/utils/utils";
import { sizingKey } from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { useSizingUnits } from "@/composables/units/useSizingUnits";
import { getMultiScenarioPumpChartData } from "@/libs/charting/multiScenarioPump/useMultiScenarioPumpChartData";
import { useComputedDomains } from "@/composables/useComputedDomains";
import { useOperatingPointsInAggregatedDataSets } from "@/composables/useOperatingPointsInAggregatedDataSets";
import { Sizing_Pumping_Type_Enum } from "gql/api";
import { PressureUnits } from "@/libs/units/pressure";

const props = defineProps<{
  title?: string;
  drawLegend?: boolean;
  isStatic?: boolean;
  isHorizontal?: boolean;
  sizing?: Sizing | null;
  scenarios: Array<ScenarioMap[string]>;
}>();

const sizing = props.sizing ? ref(props.sizing) : injectStrict(sizingKey);
const domState = ref<DOMState>();
const readyToDraw = computed(() =>
  producedTdhDataSets.value.some(dataSet => dataSet.points.length)
);

const { targetLargeLengthUnit, targetLiquidFlowUnit, targetPressureUnit } =
  useSizingUnits(sizing);

const ddvTickFrequencies = inject<{ x: string; y: string }>(
  "ddvTickFrequencies"
);

const areScenariosEspShore = computed(
  () => sizing.value?.pumping_type === Sizing_Pumping_Type_Enum.EspOnShore
);

const chartData = computed(() =>
  getMultiScenarioPumpChartData(
    props.scenarios,
    targetLiquidFlowUnit.value,
    targetLargeLengthUnit.value,
    targetPressureUnit.value,
    areScenariosEspShore.value
  )
);

const yUnit = computed(() => chartData.value?.context.targetYUnit);
const xUnitLabel = computed(() => chartData.value?.context.targetXUnitLabel);

const yUnitLabel = computed(() => {
  const isYUnitPSI =
    yUnit.value === PressureUnits.PoundsPerSquareInchAbsolute ||
    yUnit.value === PressureUnits.PoundsPerSquareInchGauge;
  if (areScenariosEspShore.value) {
    return `Head (${yUnit.value.inSituLabel || yUnit.value.shortLabel || yUnit.value.label})`;
  } else if (isYUnitPSI) {
    return `Differential Pressure (${PressureUnits.PoundsPerSquareInchDifferential.inSituLabel || PressureUnits.PoundsPerSquareInchDifferential.shortLabel || PressureUnits.PoundsPerSquareInchDifferential.label})`;
  } else {
    return `Differential Pressure (${yUnit.value.inSituLabel || yUnit.value.shortLabel || yUnit.value.label})`;
  }
});

const operatingPoints = computed(() => chartData.value?.operatingPoints || []);

const producedTdhDataSets = computed<DataSet[]>(
  () => chartData.value?.producedTdhDataSets || []
);

const requiredTdhDataSets = computed<DataSet[]>(
  () => chartData.value.requiredTdhDataSets || []
);

const tdhDataSets = computed(() => [
  ...requiredTdhDataSets.value,
  ...producedTdhDataSets.value
]);

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const activeDataSets = ref(producedTdhDataSets.value.map(_ => true));

const aggregatedDataSets = useOperatingPointsInAggregatedDataSets({
  operatingPoints,
  tableDataSets: tdhDataSets
});
watch(producedTdhDataSets, newVal => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  activeDataSets.value = newVal.map(_ => true);
});

const fakeInactiveDataSets = ref([]);

const tdhAbsoluteScale = ref(true);
const { aggregateX, aggregateY } = useAggregateDomains(
  aggregatedDataSets,
  fakeInactiveDataSets,
  tdhAbsoluteScale
);

const computedDomains = useComputedDomains(aggregatedDataSets, 0.1, 0.1);

const paddedDomainY = computed<NumericDomain>(() => {
  if (isNumberDomain(aggregateY.value)) {
    return {
      min: computedDomains.value.minY,
      max: aggregateY.value.max * 1.1
    };
  }
  return { min: 0, max: 0 };
});

const reverseX = computed(() => false);
const reverseY = computed(() => true);

const formatValue = getDefaultValueFormatter();

const targetX = computed<NumericDomain>(() =>
  fromRange(domState.value?.plotWidth ?? 0)
);
const targetY = computed<NumericDomain>(() =>
  fromRange(domState.value?.plotHeight ?? 0)
);

const xScaler = useLinearScaler(aggregateX, targetX, reverseX);
const yScaler = useLinearScaler(paddedDomainY, targetY, reverseY);
</script>

<style lang="scss" scoped>
.multiscenario-pump-chart {
  display: grid;
  grid-template-columns: 3fr max-content;

  &.is-static {
    :deep(.ddv) {
      &.isPlotHovered {
        cursor: auto !important;
      }
    }
  }

  &.is-horizontal {
    grid-template-columns: 1fr;
  }
}
</style>
